/**
 * « Parrainage » (SPEC MC10) — maquette 17-compte (colonne de droite) et 22-modale-parrainage.
 * Logique = UserProfile.tsx Sponsorsip() et ReferralInviteModal() : mêmes textes (config publique
 * GET referral/active-campaigns, campagne par défaut), même lien, même envoi
 * POST client/sendreferralemail, même partage natif.
 */
import { Box, Text } from '@chakra-ui/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { ApiClientException } from 'utils/apiClientException';
import { HTTP, useCopyToClipboard } from 'utils/http';
import { useProfileCopy, useReferralLink } from '../hooks';
import { Icon } from './Icon';
import { typo, E_MAIL } from '../../../lib/typo';
import { AccountModal, ModalButton, ModalField, ModalInput, ModalLabel, ModalLede, ModalTextarea } from './Ui';

/** = UserProfile.tsx REFERRAL_MESSAGE_MAX_LENGTH (aligné sur Client::REFERRAL_MESSAGE_MAX_LENGTH). */
const REFERRAL_MESSAGE_MAX_LENGTH = 500;
/** Formulaire relié au bouton d’envoi du pied de modale (rendu hors du <form>). */
const FORM_ID = 'mmv2-acct-referral-form';

export function ReferralCard() {
  const copyToClipboard = useCopyToClipboard();
  const referralLink = useReferralLink();
  const profileCopy = useProfileCopy();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <section className="mmv2-card mmv2-card--pad mmv2-acct-side" id="parrainage" aria-labelledby="mmv2-acct-referral">
      <h2 className="mmv2-acct-kicker" id="mmv2-acct-referral">
        Parrainage
      </h2>
      {/* Textes du BO (et valeurs par défaut partagées avec la v1) dans la typographie de la v2 : ’, « : » insécable. */}
      <h3 className="mmv2-acct-side__title">{typo(profileCopy.heading)}</h3>
      <p className="mmv2-acct-side__promise">{typo(profileCopy.subtitle)}</p>
      <p className="mmv2-small mmv2-acct-side__intro">{typo(profileCopy.stepsIntro)}</p>
      <ul className="mmv2-acct-checklist">
        {profileCopy.steps
          .filter((step) => step !== '')
          .map((step, i) => (
            <li key={`${i}-${step}`}>
              <Icon name="check" size={15} />
              <span>{typo(step)}</span>
            </li>
          ))}
      </ul>
      <div className="mmv2-field mmv2-acct-field">
        <label className="mmv2-label" htmlFor="mmv2-acct-reflink">
          Votre lien de parrainage
        </label>
        <div className="mmv2-acct-copyrow">
          <input
            id="mmv2-acct-reflink"
            className="mmv2-input mmv2-acct-copyrow__input"
            readOnly
            value={referralLink ?? ''}
            onFocus={(e) => e.currentTarget.select()}
          />
          <button
            type="button"
            className="mmv2-btn mmv2-btn--dark mmv2-btn--sm mmv2-acct-copyrow__btn"
            aria-label="Copier le lien"
            title="Copier le lien"
            onClick={() => {
              const input = document.getElementById('mmv2-acct-reflink') as HTMLInputElement | null;
              copyToClipboard(input ?? undefined, 'Lien copié');
            }}
          >
            <Icon name="copy" size={15} />
          </button>
        </div>
      </div>
      <button
        type="button"
        className="mmv2-btn mmv2-btn--primary mmv2-btn--block mmv2-acct-side__cta"
        onClick={() => setInviteOpen(true)}
        aria-haspopup="dialog"
      >
        <Icon name="mail" size={15} />
        Inviter vos proches
      </button>
      <ReferralInviteModal isOpen={inviteOpen} onClose={() => setInviteOpen(false)} />
    </section>
  );
}

interface ReferralFormData {
  email: string;
  message: string;
}

/** = UserProfile.tsx ReferralInviteModal() */
function ReferralInviteModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { register, handleSubmit, setError, formState, reset, watch } = useForm<ReferralFormData>({
    defaultValues: { email: '', message: '' },
  });
  const referralLink = useReferralLink();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const message = watch('message') ?? '';
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  function close() {
    onClose();
    setSentTo(null);
    reset();
  }

  function inviteAnother() {
    setSentTo(null);
    reset();
  }

  function onSubmit(data: ReferralFormData) {
    return HTTP.post('/client/sendreferralemail', {
      email: data.email.trim(),
      message: data.message.trim(),
      link: referralLink,
    })
      .send()
      .then(() => setSentTo(data.email.trim()))
      .catch((error: unknown) => {
        // Erreur au format WordPress ({ code, message } : garde-fou du staging) ou réseau : getMessage() vide ou
        // absent ; texte de repli, jamais une alerte vide (revue r4).
        setError('root', {
          message:
            (error instanceof ApiClientException && error.getMessage()) ||
            'L’invitation n’a pas pu être envoyée. Réessayez dans un instant.',
        });
      });
  }

  function shareLink() {
    if (!referralLink || !canShare) return;
    navigator.share({ title: 'Inviter vos proches', url: referralLink }).catch(() => undefined);
  }

  // Pied commun (AccountModal) : mêmes règles de mise en page que les autres modales de Mon compte (revue du 05/10).
  const footer = sentTo ? (
    <>
      <ModalButton tone="quiet" onClick={inviteAnother}>
        Inviter quelqu’un d’autre
      </ModalButton>
      <ModalButton tone="primary" onClick={close}>
        Terminer
      </ModalButton>
    </>
  ) : (
    <>
      {canShare ? (
        <ModalButton tone="quiet" onClick={shareLink} isDisabled={!referralLink}>
          Partager le lien
        </ModalButton>
      ) : null}
      <ModalButton
        tone="primary"
        type="submit"
        form={FORM_ID}
        isLoading={formState.isSubmitting}
        loadingText="Envoi…"
        leftIcon={<Icon name="mail" size={15} />}
      >
        Envoyer l’invitation
      </ModalButton>
    </>
  );

  return (
    <AccountModal isOpen={isOpen} onClose={close} title="Inviter vos proches" footer={footer}>
      {sentTo ? (
        <Box
          role="status"
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="8px"
          py="8px"
          textAlign="center"
          sx={{ textWrap: 'balance' }}
        >
          <Box display="grid" placeItems="center" w="64px" h="64px" mb="8px" borderRadius="999px" bg="mmv2.green50" color="mmv2.greenText">
            <Icon name="check" size={30} stroke={2.4} />
          </Box>
          <Text fontFamily="mmv2Display" fontSize="19px" fontWeight={600} letterSpacing={0} sx={{ wordSpacing: "0.1em" }}>
            Invitation envoyée
          </Text>
          <Text>
            <strong>{sentTo}</strong> va recevoir un {E_MAIL} avec votre lien de parrainage.
          </Text>
          <Text fontSize="13px" color="mmv2.ink3">
            S’il vous répond, sa réponse arrivera directement dans votre boîte de réception.
          </Text>
        </Box>
      ) : (
        // Validation par le formulaire (message v2 sous le champ), pas par la bulle native du navigateur (revue du 05/10).
        <form id={FORM_ID} noValidate onSubmit={handleSubmit(onSubmit)}>
          <ModalLede>Votre proche reçoit un {E_MAIL} avec votre lien de parrainage pour créer son compte.</ModalLede>
          <ModalField mt="0">
            <ModalLabel htmlFor="mmv2-acct-inv-email">Son adresse {E_MAIL}</ModalLabel>
            <ModalInput
              id="mmv2-acct-inv-email"
              type="email"
              autoComplete="off"
              placeholder="prenom@exemple.com"
              aria-required="true"
              aria-invalid={formState.errors.email ? true : undefined}
              aria-describedby={formState.errors.email ? 'mmv2-acct-inv-email-error' : undefined}
              {...register('email', {
                required: `Indiquez son adresse ${E_MAIL}.`,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: `Cette adresse ${E_MAIL} n’est pas valide.` },
              })}
            />
            {formState.errors.email ? (
              <Text id="mmv2-acct-inv-email-error" mt="6px" fontSize="12.5px" lineHeight="1.5" color="mmv2.redText">
                {formState.errors.email.message}
              </Text>
            ) : null}
          </ModalField>
          <ModalField>
            <ModalLabel htmlFor="mmv2-acct-inv-msg">Un petit mot (facultatif)</ModalLabel>
            <ModalTextarea
              id="mmv2-acct-inv-msg"
              rows={3}
              maxLength={REFERRAL_MESSAGE_MAX_LENGTH}
              placeholder={'Viens tester un cours avec moi\u00a0!'}
              aria-describedby="mmv2-acct-inv-count"
              {...register('message', { maxLength: REFERRAL_MESSAGE_MAX_LENGTH })}
            />
            <Text id="mmv2-acct-inv-count" mt="4px" fontSize="11.5px" color="mmv2.ink3" textAlign="right">
              {message.length}/{REFERRAL_MESSAGE_MAX_LENGTH}
            </Text>
          </ModalField>
          {formState.errors.root ? (
            <Text role="alert" mt="8px" fontSize="13px" color="mmv2.redText">
              {formState.errors.root.message}
            </Text>
          ) : null}
        </form>
      )}
    </AccountModal>
  );
}
